/**
 * Agent 对比矩阵组件 V1.0
 *
 * 统一展示多个验证 Agent 对每个产品的提取意见，供三个展示点复用：
 * - APReconciliation 的 ValidationResultModal（完整模式）
 * - APReconciliation 的 FlightValidationCell Popover（紧凑模式）
 * - APBatchReconciliation 的 DetailDrawer（紧凑模式）
 *
 * 数据来源：后端 _vote_for_flight 返回的 products_matrix 字段
 *
 * 矩阵结构：
 * | 产品名称 | 标准值 | Agent#1 | Agent#2 | Agent#3 | 判定 |
 * |---------|-------|---------|---------|---------|-----|
 * | Pushback| 750   | 750 ✓   | 750 ✓   | — 未提取 ✗ | 异常 |
 */
import React from "react";
import { Typography, Tag, Tooltip, Empty } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    MinusCircleOutlined,
    SwapOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// 费用类型友好名称映射
const COST_TYPE_LABELS = {
    CATERING: "配餐",
    FUEL: "燃油",
    PERMIT: "许可",
    CREW_TRANSPORT: "机组接送",
    HOTAC: "酒店",
    GROUND_HANDLING: "地面服务",
    OTHER: "其他",
};

/**
 * 格式化金额
 */
const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * 从 validationResult 中提取 products_matrix 数据
 *
 * 优先使用后端 V6.0 直接提供的 products_matrix，
 * 降级到从 votes 中自行构建（兼容旧数据）。
 */
export const extractProductsMatrix = (validationResult) => {
    if (!validationResult) return null;

    // 优先使用后端直接提供的 products_matrix（V6.0）
    if (validationResult.products_matrix && Object.keys(validationResult.products_matrix).length > 0) {
        return validationResult.products_matrix;
    }

    // 降级：从 votes 中自行构建（兼容旧格式）
    const votes = validationResult.votes || [];
    if (votes.length === 0) {
        // 再次降级：从 products 直接构建（单任务结果）
        const products = validationResult.products || validationResult.products_comparison;
        if (products && Object.keys(products).length > 0) {
            const matrix = {};
            Object.entries(products).forEach(([code, pData]) => {
                const extracted = pData.extracted;
                const value = Array.isArray(extracted) && extracted.length > 0 ? extracted[0] : (typeof extracted === "number" ? extracted : 0);
                matrix[code] = {
                    standard: pData.standard ?? null,
                    product_name: pData.product_name || code,
                    cost_type: pData.cost_type || null,
                    match: pData.match !== false,
                    agent_results: [{
                        task_id: "single",
                        value,
                        match: pData.match !== false,
                        missing: value === 0 && (pData.standard ?? 0) > 0,
                    }],
                };
            });
            return matrix;
        }
        return null;
    }

    // 从 votes 重建
    const matrix = {};

    // 第一遍：收集所有产品
    votes.forEach((vote) => {
        const products = vote.products || {};
        Object.entries(products).forEach(([code, pData]) => {
            if (!matrix[code]) {
                matrix[code] = {
                    standard: pData.standard ?? null,
                    product_name: pData.product_name || code,
                    cost_type: pData.cost_type || null,
                    match: true,
                    agent_results: [],
                };
            }
            if (matrix[code].standard === null && pData.standard != null) {
                matrix[code].standard = pData.standard;
            }
        });
    });

    // 第二遍：填充每个 vote 的结果
    votes.forEach((vote) => {
        const taskId = vote.task_id || "unknown";
        const products = vote.products || {};

        Object.keys(matrix).forEach((code) => {
            if (code in products) {
                const pData = products[code];
                const extracted = pData.extracted;
                const value = Array.isArray(extracted) && extracted.length > 0 ? extracted[0] : (typeof extracted === "number" ? extracted : 0);
                const match = pData.match !== false;

                matrix[code].agent_results.push({
                    task_id: taskId,
                    value,
                    match,
                    missing: false,
                    execution_failed: Boolean(pData.execution_failed || vote.execution_failed),
                    failure_reason: pData.failure_reason || vote.failure_reason,
                    execution_id: pData.execution_id || vote.execution_id,
                    execution_evidence: pData.execution_evidence || vote.execution_evidence,
                    terminal_error_type: pData.terminal_error_type || vote.terminal_error_type,
                });

                if (!match || pData.execution_failed || vote.execution_failed) matrix[code].match = false;
            } else {
                // Agent 没有该产品的数据
                matrix[code].agent_results.push({
                    task_id: taskId,
                    value: 0,
                    match: false,
                    missing: true,
                    execution_failed: Boolean(vote.execution_failed),
                    failure_reason: vote.failure_reason,
                    execution_id: vote.execution_id,
                    execution_evidence: vote.execution_evidence,
                    terminal_error_type: vote.terminal_error_type,
                });
                if (matrix[code].standard && matrix[code].standard > 0.01) {
                    matrix[code].match = false;
                }
            }
        });
    });

    return Object.keys(matrix).length > 0 ? matrix : null;
};

/**
 * Agent 对比矩阵组件
 *
 * @param {Object} props
 * @param {Object} props.productsMatrix - products_matrix 数据（由 extractProductsMatrix 提取）
 * @param {boolean} props.compact - 是否使用紧凑模式（Popover/Drawer 中使用）
 * @param {number} props.voteCount - 验证次数（用于表头）
 * @param {string[]} props.relatedProductKeys - "Agent 实际记账编码"关联行的矩阵 key（由 scope 机制判定），
 *   这些行紧跟标准行下方分组展示，判定列标注"编码不一致·金额一致"；未传时行为与之前完全一致
 */
const RELATED_ALIAS_AMOUNT_TOLERANCE = 0.01;

const AgentComparisonMatrix = ({ productsMatrix, compact = false, voteCount = 0, relatedProductKeys = [] }) => {
    if (!productsMatrix || Object.keys(productsMatrix).length === 0) {
        return <Empty description="无产品验证详情" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    // 将 matrix 转换为排序后的数组，错误项优先；关联行紧跟标准行之后保持分组
    const relatedKeySet = new Set(relatedProductKeys || []);
    const items = Object.entries(productsMatrix).map(([code, data]) => ({
        code,
        ...data,
        isRelatedAlias: relatedKeySet.has(code),
    }));

    items.sort((a, b) => {
        // 标准行在前，关联行紧随其后
        if (a.isRelatedAlias !== b.isRelatedAlias) return a.isRelatedAlias ? 1 : -1;
        // 不匹配的排前面
        if (a.match !== b.match) return a.match ? 1 : -1;
        // 有缺失的排前面
        const aMissing = a.agent_results?.some((r) => r.missing) ? 0 : 1;
        const bMissing = b.agent_results?.some((r) => r.missing) ? 0 : 1;
        return aMissing - bMissing;
    });

    // 获取 Agent 数量
    const agentCount = items[0]?.agent_results?.length || 0;

    // 标准行的标准值，用于判定关联行"金额一致"
    const primaryItem = items.find((item) => !item.isRelatedAlias);
    const primaryStandard = Number.isFinite(Number(primaryItem?.standard)) ? Number(primaryItem.standard) : null;

    const isAliasAmountConsistent = (item) => {
        if (primaryStandard === null) return false;
        return (item.agent_results || []).some((result) => !result?.missing && !result?.execution_failed && Math.abs(Number(result?.value) - primaryStandard) <= RELATED_ALIAS_AMOUNT_TOLERANCE);
    };

    // ===== 渲染单个 Agent 值的单元格 =====
    const renderAgentCell = (result, idx) => {
        if (!result) return <span style={{ color: "#bfbfbf" }}>—</span>;

        if (result.execution_failed) {
            const executionText = result.execution_id ? `\nExecution ID: ${result.execution_id}` : "\n未记录外部 execution id，无法确认是否成功发起";
            return (
                <Tooltip title={`${result.failure_reason || `Agent #${idx + 1} 执行失败，已按未匹配计入汇总`}${executionText}`}>
                    <span style={{
                        color: "#d46b08",
                        fontSize: compact ? 10 : 11,
                        fontStyle: "italic",
                        fontWeight: 600,
                    }}>
                        <ExclamationCircleOutlined style={{ fontSize: 9, marginRight: 2 }} />
                        失败
                    </span>
                </Tooltip>
            );
        }

        if (result.missing) {
            return (
                <Tooltip title={`Agent #${idx + 1} 未提取到此产品`}>
                    <span style={{
                        color: "#bfbfbf",
                        fontSize: compact ? 10 : 11,
                        fontStyle: "italic",
                    }}>
                        <MinusCircleOutlined style={{ fontSize: 9, marginRight: 2 }} />
                        未提取
                    </span>
                </Tooltip>
            );
        }

        const color = result.match ? "#52c41a" : "#ff4d4f";
        const fontWeight = result.match ? undefined : 600;

        return (
            <Tooltip title={`Agent #${idx + 1} (${result.task_id || "?"})\n提取值: ${formatAmount(result.value)}`}>
                <span style={{
                    color,
                    fontWeight,
                    fontSize: compact ? 10 : 11,
                }}>
                    {formatAmount(result.value)}
                </span>
            </Tooltip>
        );
    };

    // ===== 渲染判定列 =====
    const renderVerdict = (item) => {
        // 关联行（Agent 实际记账编码）：编码与标准答案不一致，但金额可能完全一致
        if (item.isRelatedAlias) {
            const amountConsistent = isAliasAmountConsistent(item);
            return (
                <Tooltip title="Agent 将金额提取到了该编码下，与当前行标准编码不一致">
                    <Tag color="warning" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                        <SwapOutlined /> {amountConsistent ? "编码不一致·金额一致" : "编码不一致"}
                    </Tag>
                </Tooltip>
            );
        }

        const hasExecutionFailure = item.agent_results?.some((r) => r.execution_failed);
        const hasMissing = item.agent_results?.some((r) => r.missing);
        const hasValueMismatch = item.agent_results?.some((r) => !r.missing && !r.match);

        if (item.match) {
            return (
                <Tag color="success" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                    <CheckCircleOutlined /> 一致
                </Tag>
            );
        }

        if (hasMissing && !hasValueMismatch) {
            if (hasExecutionFailure) {
                return (
                    <Tag color="error" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                        <ExclamationCircleOutlined /> 执行失败
                    </Tag>
                );
            }
            return (
                <Tag color="warning" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                    <MinusCircleOutlined /> 部分缺失
                </Tag>
            );
        }

        return (
            <Tag color="error" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                <CloseCircleOutlined /> 不一致
            </Tag>
        );
    };

    // ===== 紧凑模式 =====
    if (compact) {
        // 关联行的判定标签较长（"编码不一致·金额一致"），存在关联行时加宽判定列
        const verdictColumnWidth = items.some((item) => item.isRelatedAlias) ? 124 : 70;
        const compactGridTemplate = `1fr 68px ${Array(agentCount).fill("68px").join(" ")} ${verdictColumnWidth}px`;
        return (
            <div style={{ fontSize: 11 }}>
                {/* 图例行 */}
                <div style={{
                    display: "flex",
                    gap: 10,
                    marginBottom: 6,
                    fontSize: 10,
                    color: "#8c8c8c",
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 4,
                }}>
                    <span><CheckCircleOutlined style={{ color: "#52c41a" }} /> 匹配</span>
                    <span><CloseCircleOutlined style={{ color: "#ff4d4f" }} /> 不一致</span>
                    <span><ExclamationCircleOutlined style={{ color: "#d46b08" }} /> 执行失败</span>
                    <span><MinusCircleOutlined style={{ color: "#bfbfbf" }} /> 未提取</span>
                    {voteCount > 0 && (
                        <span style={{ marginLeft: "auto" }}>{voteCount} 次独立验证</span>
                    )}
                </div>

                {/* 表头 */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: compactGridTemplate,
                    gap: 2,
                    padding: "4px 6px",
                    background: "#fafafa",
                    borderRadius: "4px 4px 0 0",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#8c8c8c",
                }}>
                    <span>产品</span>
                    <span style={{ textAlign: "right" }}>标准值</span>
                    {Array.from({ length: agentCount }, (_, i) => (
                        <span key={i} style={{ textAlign: "right" }}>#{i + 1}</span>
                    ))}
                    <span style={{ textAlign: "center" }}>判定</span>
                </div>

                {/* 数据行 */}
                {items.map((item) => (
                    <Tooltip
                        key={item.code}
                        title={
                            <div style={{ fontSize: 11 }}>
                                <div><strong>产品编码：</strong>{item.code}</div>
                                {item.cost_type && (
                                    <div><strong>费用类型：</strong>{COST_TYPE_LABELS[item.cost_type] || item.cost_type}</div>
                                )}
                            </div>
                        }
                        placement="left"
                    >
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: compactGridTemplate,
                            gap: 2,
                            padding: "5px 6px",
                            background: item.isRelatedAlias ? "#fffbe6" : item.match ? "#f6ffed" : "#fff2f0",
                            border: `1px solid ${item.isRelatedAlias ? "#ffe58f" : item.match ? "#b7eb8f" : "#ffccc7"}`,
                            borderTop: "none",
                            alignItems: "center",
                            cursor: "default",
                        }}>
                            {/* 产品名称（关联行缩进并标注"Agent 实际编码"） */}
                            <div style={{ minWidth: 0 }}>
                                <Text
                                    ellipsis
                                    style={{
                                        fontSize: 10,
                                        color: item.match || item.isRelatedAlias ? undefined : "#262626",
                                    }}
                                >
                                    {item.isRelatedAlias ? `└ ${item.product_name}` : item.product_name}
                                </Text>
                                {item.isRelatedAlias && (
                                    <div>
                                        <Text style={{ fontSize: 9, color: "#d48806" }}>Agent 实际编码</Text>
                                    </div>
                                )}
                            </div>

                            {/* 标准值 */}
                            <Text style={{
                                fontSize: 10,
                                textAlign: "right",
                                color: "#1890ff",
                                fontWeight: 500,
                            }}>
                                {item.standard != null ? formatAmount(item.standard) : "—"}
                            </Text>

                            {/* Agent 值 */}
                            {(item.agent_results || []).map((result, idx) => (
                                <div key={idx} style={{ textAlign: "right" }}>
                                    {renderAgentCell(result, idx)}
                                </div>
                            ))}

                            {/* 判定 */}
                            <div style={{ textAlign: "center" }}>
                                {renderVerdict(item)}
                            </div>
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    }

    // ===== 完整模式（Modal 中使用） =====
    // 关联行的判定标签较长（"编码不一致·金额一致"），存在关联行时加宽判定列
    const fullVerdictColumnWidth = items.some((item) => item.isRelatedAlias) ? 130 : 80;
    const fullGridTemplate = `minmax(100px, 1.5fr) 80px ${Array(agentCount).fill("80px").join(" ")} ${fullVerdictColumnWidth}px`;
    return (
        <div style={{ fontSize: 12 }}>
            {/* 标题和图例 */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
            }}>
                <Text strong style={{ fontSize: 13 }}>
                    产品级 Agent 对比矩阵
                </Text>
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#8c8c8c" }}>
                    <span><CheckCircleOutlined style={{ color: "#52c41a" }} /> 匹配</span>
                    <span><CloseCircleOutlined style={{ color: "#ff4d4f" }} /> 不一致</span>
                    <span><ExclamationCircleOutlined style={{ color: "#d46b08" }} /> 执行失败</span>
                    <span><MinusCircleOutlined style={{ color: "#bfbfbf" }} /> 未提取</span>
                    {voteCount > 0 && <span>{voteCount} 次独立验证</span>}
                </div>
            </div>

            {/* 表头 */}
            <div style={{
                display: "grid",
                gridTemplateColumns: fullGridTemplate,
                gap: 4,
                padding: "6px 8px",
                background: "#fafafa",
                borderRadius: "6px 6px 0 0",
                fontWeight: 600,
                fontSize: 11,
                color: "#595959",
                borderBottom: "2px solid #d9d9d9",
            }}>
                <span>产品名称</span>
                <span style={{ textAlign: "right" }}>标准值</span>
                {Array.from({ length: agentCount }, (_, i) => (
                    <span key={i} style={{ textAlign: "right" }}>Agent #{i + 1}</span>
                ))}
                <span style={{ textAlign: "center" }}>判定</span>
            </div>

            {/* 数据行 */}
            {items.map((item, rowIdx) => (
                <Tooltip
                    key={item.code}
                    title={
                        <div style={{ fontSize: 11 }}>
                            <div><strong>产品编码：</strong>{item.code}</div>
                            {item.cost_type && (
                                <div><strong>费用类型：</strong>{COST_TYPE_LABELS[item.cost_type] || item.cost_type}</div>
                            )}
                            <div style={{ marginTop: 4 }}>
                                {(item.agent_results || []).map((r, i) => (
                                    <div key={i}>
                                        Agent #{i + 1} ({r.task_id}): {r.execution_failed ? "执行失败" : (r.missing ? "未提取到" : formatAmount(r.value))} {r.execution_failed ? "!" : (r.match ? "✓" : "✗")}
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                    placement="left"
                >
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: fullGridTemplate,
                        gap: 4,
                        padding: "7px 8px",
                        background: item.isRelatedAlias ? "#fffbe6" : item.match ? (rowIdx % 2 === 0 ? "#f6ffed" : "#f0fbe4") : (rowIdx % 2 === 0 ? "#fff2f0" : "#ffe8e6"),
                        border: `1px solid ${item.isRelatedAlias ? "#ffe58f" : item.match ? "#b7eb8f" : "#ffccc7"}`,
                        borderTop: "none",
                        alignItems: "center",
                        transition: "background 0.15s",
                        cursor: "default",
                    }}>
                        {/* 产品名称 */}
                        <div>
                            <Text
                                ellipsis
                                style={{
                                    fontSize: 11,
                                    fontWeight: !item.match && !item.isRelatedAlias ? 500 : undefined,
                                }}
                            >
                                {item.isRelatedAlias ? `└ ${item.product_name}` : item.product_name}
                            </Text>
                            {item.isRelatedAlias && (
                                <div>
                                    <Text style={{ fontSize: 10, color: "#d48806" }}>Agent 实际编码</Text>
                                </div>
                            )}
                            {item.cost_type && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        {COST_TYPE_LABELS[item.cost_type] || item.cost_type}
                                    </Text>
                                </div>
                            )}
                        </div>

                        {/* 标准值 */}
                        <Text style={{
                            fontSize: 11,
                            textAlign: "right",
                            color: "#1890ff",
                            fontWeight: 600,
                        }}>
                            {item.standard != null ? formatAmount(item.standard) : "—"}
                        </Text>

                        {/* Agent 值 */}
                        {(item.agent_results || []).map((result, idx) => (
                            <div key={idx} style={{ textAlign: "right" }}>
                                {renderAgentCell(result, idx)}
                            </div>
                        ))}

                        {/* 判定 */}
                        <div style={{ textAlign: "center" }}>
                            {renderVerdict(item)}
                        </div>
                    </div>
                </Tooltip>
            ))}
        </div>
    );
};

export default AgentComparisonMatrix;
